import React, { useState, useEffect } from 'react';
import useAuth from '../hooks/useAuth';
import { conversionsApi, metaApi, googleAdsApi } from '../services/api';
import toast from 'react-hot-toast';

const STATUS_LABELS = {
  PENDING: { label: 'Pendente', color: 'bg-slate-100 text-slate-600' },
  WAITING_WA: { label: 'Aguard. WhatsApp', color: 'bg-yellow-100 text-yellow-700' },
  MESSAGE_INITIATED: { label: 'Mensagem Recebida', color: 'bg-blue-100 text-blue-700' },
  QUALIFIED: { label: 'Qualificado', color: 'bg-purple-100 text-purple-700' },
  CONVERTED: { label: 'Convertido', color: 'bg-green-100 text-green-700' },
  LOST_NO_MESSAGE: { label: 'Perdido', color: 'bg-red-100 text-red-500' },
  LOST_OPT_OUT: { label: 'Opt-out', color: 'bg-red-100 text-red-500' }
};

const FUNNEL_MODES = [
  { value: 'LEAD', label: 'Meio de Funil (Lead)' },
  { value: 'PURCHASE', label: 'Fundo de Funil (Purchase)' },
  { value: 'BOTH', label: 'Ambos' }
];

const REDIRECT_TYPES = [
  { value: 'WA_LINK', label: 'wa.me (link simples)' },
  { value: 'WA_CLOUD_API', label: 'WhatsApp Cloud API' },
  { value: 'EXTERNAL_URL', label: 'URL Externa' }
];

const StatCard = ({ icon, label, value, color = 'text-primary' }) => (
  <div className="bg-surface-container-low rounded-xl p-6 shadow-[0_4px_16px_rgba(25,28,30,0.04)]">
    <div className="flex items-center gap-3 mb-3">
      <span className={`material-symbols-outlined ${color}`}>{icon}</span>
      <span className="text-xs font-bold text-secondary uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-3xl font-extrabold text-on-surface font-headline">{value}</p>
  </div>
);

export default function Conversions() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('links');

  // Links state
  const [links, setLinks] = useState([]);
  const [isLoadingLinks, setIsLoadingLinks] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [adAccounts, setAdAccounts] = useState([]);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [wabaPhones, setWabaPhones] = useState([]);
  const [linkForm, setLinkForm] = useState({
    name: '', slug: '', metaAdAccountId: '', googleAdsAccountId: '',
    whatsappPhoneNumberId: '', pixelId: '', gtmId: '',
    funnelMode: 'LEAD', redirectType: 'WA_LINK',
    redirectUrl: '', whatsappNumber: '', prefilledMessage: 'Olá! Tenho interesse.',
    metaCampaignId: '', metaCampaignName: '', metaAdsetId: '', metaAdsetName: '',
    metaAdId: '', metaAdName: '',
    chatwootAccountId: '', chatwootInboxId: ''
  });
  const [campaigns, setCampaigns] = useState([]);
  const [adsets, setAdsets] = useState([]);
  const [ads, setAds] = useState([]);

  // Leads state
  const [leads, setLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  // WhatsApp state
  const [wabas, setWabas] = useState([]);
  const [isLoadingWabas, setIsLoadingWabas] = useState(false);
  const [isWabaModalOpen, setIsWabaModalOpen] = useState(false);
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [selectedWabaId, setSelectedWabaId] = useState(null);
  const [wabaForm, setWabaForm] = useState({ wabaId: '', name: '', bsp: 'meta_direct', webhookVerifyToken: '' });
  const [phoneForm, setPhoneForm] = useState({ phoneNumberId: '', displayPhone: '', accessToken: '' });

  // Stats state
  const [stats, setStats] = useState(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  // Chatwoot state
  const [chatwootAccounts, setChatwootAccounts] = useState([]);
  const [isLoadingChatwoot, setIsLoadingChatwoot] = useState(false);
  const [isCwModalOpen, setIsCwModalOpen] = useState(false);
  const [cwForm, setCwForm] = useState({ chatwootAccountId: '', name: '', apiAccessToken: '' });
  const [chatwootInboxes, setChatwootInboxes] = useState([]);
  const [chatwootWebhookUrl, setChatwootWebhookUrl] = useState('');

  useEffect(() => {
    fetchLinks();
    fetchAdAccounts();
  }, []);

  useEffect(() => {
    if (activeTab === 'leads') fetchLeads();
    if (activeTab === 'whatsapp') fetchWabas();
    if (activeTab === 'stats') fetchStats();
    if (activeTab === 'crm') fetchChatwootAccounts();
  }, [activeTab, statusFilter]);

  const fetchLinks = async () => {
    setIsLoadingLinks(true);
    try {
      const res = await conversionsApi.getLinks();
      setLinks(res.data.links || []);
    } catch (e) {
      toast.error('Erro ao buscar links');
    } finally {
      setIsLoadingLinks(false);
    }
  };

  const fetchAdAccounts = async () => {
    try {
      const [metaRes, googleRes] = await Promise.allSettled([
        metaApi.getAllAccountsFromDb(),
        googleAdsApi.getAllAccountsFromDb()
      ]);
      if (metaRes.status === 'fulfilled') setAdAccounts(metaRes.value.data?.accounts || []);
      if (googleRes.status === 'fulfilled') setGoogleAccounts(googleRes.value.data?.accounts || []);
    } catch (e) {}
  };

  const fetchWabaPhones = async () => {
    try {
      const res = await conversionsApi.getWabas();
      const phones = (res.data.wabas || []).flatMap(w =>
        (w.phoneNumbers || []).map(p => ({ ...p, wabaName: w.name }))
      );
      setWabaPhones(phones);
    } catch (e) {}
  };

  const fetchLeads = async () => {
    setIsLoadingLeads(true);
    try {
      const res = await conversionsApi.getLeads({ status: statusFilter || undefined });
      setLeads(res.data.leads || []);
    } catch (e) {
      toast.error('Erro ao buscar leads');
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const fetchWabas = async () => {
    setIsLoadingWabas(true);
    try {
      const res = await conversionsApi.getWabas();
      setWabas(res.data.wabas || []);
    } catch (e) {
      toast.error('Erro ao buscar WABAs');
    } finally {
      setIsLoadingWabas(false);
    }
  };

  const fetchStats = async () => {
    setIsLoadingStats(true);
    try {
      const res = await conversionsApi.getStats();
      setStats(res.data.stats);
    } catch (e) {
      toast.error('Erro ao buscar estatísticas');
    } finally {
      setIsLoadingStats(false);
    }
  };

  const fetchChatwootAccounts = async () => {
    setIsLoadingChatwoot(true);
    try {
      const [accRes, urlRes] = await Promise.allSettled([
        conversionsApi.getChatwootAccounts(),
        conversionsApi.getChatwootWebhookUrl()
      ]);
      if (accRes.status === 'fulfilled') setChatwootAccounts(accRes.value?.data?.accounts || []);
      if (urlRes.status === 'fulfilled') setChatwootWebhookUrl(urlRes.value?.data?.url || '');
    } catch (e) { toast.error('Erro ao buscar contas Chatwoot'); }
    finally { setIsLoadingChatwoot(false); }
  };

  const fetchChatwootInboxes = async (cwAccountId) => {
    if (!cwAccountId) { setChatwootInboxes([]); return; }
    try {
      const res = await conversionsApi.getChatwootInboxes(cwAccountId);
      setChatwootInboxes(res.data?.inboxes || []);
    } catch (e) { setChatwootInboxes([]); }
  };

  const handleChatwootAccountChange = (cwId) => {
    setLinkForm(f => ({ ...f, chatwootAccountId: cwId, chatwootInboxId: '' }));
    fetchChatwootInboxes(cwId);
  };

  const handleCwSubmit = async (e) => {
    e.preventDefault();
    try {
      await conversionsApi.createChatwootAccount(cwForm);
      toast.success('Conta Chatwoot adicionada');
      setIsCwModalOpen(false);
      fetchChatwootAccounts();
    } catch (err) { toast.error(err.message || 'Erro ao adicionar conta'); }
  };

  const handleSetupAttributes = async (acc) => {
    try {
      const res = await conversionsApi.setupChatwootAttributes(acc.id);
      const { attributes, webhook } = res.data;
      const attrErrors = attributes?.errors?.length || 0;
      const attrMsg = `${attributes?.created?.length ?? 0} criados, ${attributes?.skipped?.length ?? 0} já existiam`;

      if (attrErrors > 0) {
        toast.error(`${attrErrors} erro(s) nos atributos: ${attributes.errors[0]}`);
      } else if (webhook?.webhook === 'manual_required') {
        if (webhook.url) navigator.clipboard.writeText(webhook.url).catch(() => {});
        toast(`Atributos OK (${attrMsg}). Webhook requer config manual — URL copiada!`, { icon: '⚠️', duration: 6000 });
        if (webhook.url) setChatwootWebhookUrl(webhook.url);
      } else {
        const whMsg = webhook?.webhook === 'already_exists' ? 'webhook já configurado' : 'webhook criado';
        toast.success(`Setup concluído — ${attrMsg}, ${whMsg}`);
      }
    } catch (err) { toast.error(err.message || 'Erro ao configurar'); }
  };

  const fetchCampaigns = async (accountId) => {
    if (!accountId) { setCampaigns([]); setAdsets([]); setAds([]); return; }
    try {
      const res = await metaApi.getCampaigns(accountId);
      setCampaigns(res.data?.campaigns || []);
    } catch (e) { setCampaigns([]); }
    setAdsets([]);
    setAds([]);
  };

  const fetchAdsets = async (accountId, campaignId) => {
    if (!accountId || !campaignId) { setAdsets([]); setAds([]); return; }
    try {
      const res = await metaApi.getAdSets(accountId, campaignId);
      setAdsets(res.data?.adsets || []);
    } catch (e) { setAdsets([]); }
    setAds([]);
  };

  const fetchAds = async (accountId, adsetId) => {
    if (!accountId || !adsetId) { setAds([]); return; }
    try {
      const res = await metaApi.getAds(accountId, adsetId);
      setAds(res.data?.ads || []);
    } catch (e) { setAds([]); }
  };

  const handleMetaAccountChange = (accountId) => {
    setLinkForm(f => ({ ...f, metaAdAccountId: accountId, metaCampaignId: '', metaCampaignName: '', metaAdsetId: '', metaAdsetName: '', metaAdId: '', metaAdName: '' }));
    fetchCampaigns(accountId);
  };

  const handleCampaignChange = (campaignId, campaignName) => {
    setLinkForm(f => ({ ...f, metaCampaignId: campaignId, metaCampaignName: campaignName, metaAdsetId: '', metaAdsetName: '', metaAdId: '', metaAdName: '' }));
    fetchAdsets(linkForm.metaAdAccountId, campaignId);
  };

  const handleAdsetChange = (adsetId, adsetName) => {
    setLinkForm(f => ({ ...f, metaAdsetId: adsetId, metaAdsetName: adsetName, metaAdId: '', metaAdName: '' }));
    fetchAds(linkForm.metaAdAccountId, adsetId);
  };

  const LP_BASE = 'https://lps.agenciamw.com.br';

  const copyLpLink = (slug) => {
    const url = `${LP_BASE}/lp/${slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success('Link da LP copiado!'));
  };

  const openAddLink = async () => {
    await fetchWabaPhones();
    if (!chatwootAccounts.length) await fetchChatwootAccounts();
    setCampaigns([]); setAdsets([]); setAds([]); setChatwootInboxes([]);
    setEditingLink(null);
    setLinkForm({
      name: '', slug: '', metaAdAccountId: '', googleAdsAccountId: '',
      whatsappPhoneNumberId: '', pixelId: '', gtmId: '',
      funnelMode: 'LEAD', redirectType: 'WA_LINK',
      redirectUrl: '', whatsappNumber: '', prefilledMessage: 'Olá! Tenho interesse.',
      metaCampaignId: '', metaCampaignName: '', metaAdsetId: '', metaAdsetName: '',
      metaAdId: '', metaAdName: '',
      chatwootAccountId: '', chatwootInboxId: ''
    });
    setIsLinkModalOpen(true);
  };

  const openEditLink = async (link) => {
    await fetchWabaPhones();
    setEditingLink(link);
    const newForm = {
      name: link.name,
      slug: link.slug,
      metaAdAccountId: link.metaAdAccountId || '',
      googleAdsAccountId: link.googleAdsAccountId || '',
      whatsappPhoneNumberId: link.whatsappPhoneNumberId || '',
      pixelId: link.pixelId || '',
      gtmId: link.gtmId || '',
      funnelMode: link.funnelMode || 'LEAD',
      redirectType: link.redirectType || 'WA_LINK',
      redirectUrl: link.redirectUrl || '',
      whatsappNumber: link.whatsappNumber || '',
      prefilledMessage: link.prefilledMessage || '',
      metaCampaignId: link.metaCampaignId || '',
      metaCampaignName: link.metaCampaignName || '',
      metaAdsetId: link.metaAdsetId || '',
      metaAdsetName: link.metaAdsetName || '',
      metaAdId: link.metaAdId || '',
      metaAdName: link.metaAdName || '',
      chatwootAccountId: link.chatwootAccountId || '',
      chatwootInboxId: link.chatwootInboxId ? String(link.chatwootInboxId) : ''
    };
    setLinkForm(newForm);
    if (link.metaAdAccountId) {
      const cRes = await metaApi.getCampaigns(link.metaAdAccountId).catch(() => null);
      setCampaigns(cRes?.data?.campaigns || []);
      if (link.metaCampaignId) {
        const aRes = await metaApi.getAdSets(link.metaAdAccountId, link.metaCampaignId).catch(() => null);
        setAdsets(aRes?.data?.adsets || []);
        if (link.metaAdsetId) {
          const dRes = await metaApi.getAds(link.metaAdAccountId, link.metaAdsetId).catch(() => null);
          setAds(dRes?.data?.ads || []);
        }
      }
    } else {
      setCampaigns([]); setAdsets([]); setAds([]);
    }
    if (!chatwootAccounts.length) await fetchChatwootAccounts();
    if (link.chatwootAccountId) {
      await fetchChatwootInboxes(link.chatwootAccountId);
    } else {
      setChatwootInboxes([]);
    }
    setIsLinkModalOpen(true);
  };

  const handleLinkSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...linkForm,
        metaAdAccountId: linkForm.metaAdAccountId || null,
        googleAdsAccountId: linkForm.googleAdsAccountId || null,
        whatsappPhoneNumberId: linkForm.whatsappPhoneNumberId || null,
        pixelId: linkForm.pixelId || null,
        gtmId: linkForm.gtmId || null,
        redirectUrl: linkForm.redirectUrl || null,
        whatsappNumber: linkForm.whatsappNumber || null,
        prefilledMessage: linkForm.prefilledMessage || null,
        metaCampaignId: linkForm.metaCampaignId || null,
        metaCampaignName: linkForm.metaCampaignName || null,
        metaAdsetId: linkForm.metaAdsetId || null,
        metaAdsetName: linkForm.metaAdsetName || null,
        metaAdId: linkForm.metaAdId || null,
        metaAdName: linkForm.metaAdName || null,
        chatwootAccountId: linkForm.chatwootAccountId || null,
        chatwootInboxId: linkForm.chatwootInboxId ? parseInt(linkForm.chatwootInboxId) : null
      };
      if (editingLink) {
        await conversionsApi.updateLink(editingLink.id, payload);
        toast.success('Link atualizado');
      } else {
        await conversionsApi.createLink(payload);
        toast.success('Link criado');
      }
      setIsLinkModalOpen(false);
      fetchLinks();
    } catch (err) {
      toast.error(err.message || 'Erro ao salvar link');
    }
  };

  const handlePublishLink = async (id) => {
    try {
      await conversionsApi.publishLink(id);
      toast.success('Link publicado');
      fetchLinks();
    } catch (err) {
      toast.error(err.message || 'Erro ao publicar');
    }
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Desativar este link? Os leads existentes são preservados.')) return;
    try {
      await conversionsApi.deleteLink(id);
      toast.success('Link desativado');
      fetchLinks();
    } catch (err) {
      toast.error(err.message || 'Erro ao desativar');
    }
  };

  const handleLeadStatus = async (id, status) => {
    try {
      await conversionsApi.updateLeadStatus(id, status);
      toast.success('Status atualizado');
      fetchLeads();
    } catch (err) {
      toast.error(err.message || 'Erro ao atualizar status');
    }
  };

  const handleWabaSubmit = async (e) => {
    e.preventDefault();
    try {
      await conversionsApi.createWaba(wabaForm);
      toast.success('WABA criada com sucesso');
      setIsWabaModalOpen(false);
      fetchWabas();
    } catch (err) {
      toast.error(err.message || 'Erro ao criar WABA');
    }
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    try {
      await conversionsApi.addPhone(selectedWabaId, phoneForm);
      toast.success('Número adicionado');
      setIsPhoneModalOpen(false);
      fetchWabas();
    } catch (err) {
      toast.error(err.message || 'Erro ao adicionar número');
    }
  };

  const copyWebhookUrl = (userId) => {
    const url = `${window.location.origin}/api/webhooks/whatsapp/${userId}`;
    navigator.clipboard.writeText(url);
    toast.success('URL do webhook copiada!');
  };

  const tabs = [
    { id: 'links', label: 'Links de Conversão', icon: 'link' },
    { id: 'leads', label: 'Leads', icon: 'group' },
    { id: 'whatsapp', label: 'WhatsApp', icon: 'chat' },
    { id: 'crm', label: 'CRM', icon: 'support_agent' },
    { id: 'stats', label: 'Relatórios', icon: 'bar_chart' }
  ];

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-8">
        <h1 className="font-headline font-extrabold text-4xl tracking-tight text-on-surface mb-2">Rastreamento de Conversões</h1>
        <p className="text-secondary font-body">Links CTWA, captura de leads, Meta CAPI e Google OCI</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6 border-b border-outline-variant/30 overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-bold font-headline border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
              activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-secondary hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-[16px]">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Links Tab ── */}
      {activeTab === 'links' && (
        <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">link</span> Links de Conversão
            </h2>
            <button onClick={openAddLink} className="primary-pill-gradient text-white px-4 py-2 rounded-full font-bold font-headline text-sm hover:opacity-90 transition-all flex items-center gap-2">
              <span className="material-symbols-outlined text-sm">add</span> Novo Link
            </button>
          </div>

          {isLoadingLinks ? (
            <p className="py-8 text-center text-secondary">Carregando...</p>
          ) : links.length === 0 ? (
            <div className="py-12 text-center">
              <span className="material-symbols-outlined text-5xl text-slate-300 mb-3 block">link_off</span>
              <p className="text-secondary">Nenhum link criado. Clique em "Novo Link" para começar.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-outline-variant/30 text-left text-xs text-secondary">
                    <th className="pb-3">Nome / Slug</th>
                    <th className="pb-3">Modo</th>
                    <th className="pb-3">Pixel</th>
                    <th className="pb-3">Leads</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {links.map(link => (
                    <tr key={link.id} className="border-b border-outline-variant/10 text-sm">
                      <td className="py-4">
                        <p className="font-bold text-on-surface">{link.name}</p>
                        <p className="text-xs text-secondary font-mono mt-0.5">/{link.slug}</p>
                        {link.metaAdAccount && <p className="text-[10px] text-blue-600 mt-0.5">Meta: {link.metaAdAccount.name}</p>}
                        {link.metaCampaignName && <p className="text-[10px] text-purple-600 mt-0.5">Camp: {link.metaCampaignName}</p>}
                      </td>
                      <td className="py-4">
                        <span className="px-2 py-0.5 rounded-md bg-surface-container text-xs font-bold">
                          {link.funnelMode}
                        </span>
                      </td>
                      <td className="py-4 text-xs text-secondary font-mono">{link.pixelId || '—'}</td>
                      <td className="py-4 font-bold text-on-surface">{link._count?.leads ?? 0}</td>
                      <td className="py-4">
                        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${link.isActive ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                          {link.isActive ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="py-4 text-right space-x-1">
                        <button onClick={() => copyLpLink(link.slug)} title="Copiar link da LP" className="text-slate-400 hover:text-green-600 transition-colors p-1">
                          <span className="material-symbols-outlined text-[20px]">open_in_new</span>
                        </button>
                        {!link.publishedAt && (
                          <button onClick={() => handlePublishLink(link.id)} title="Publicar" className="text-slate-400 hover:text-green-600 transition-colors p-1">
                            <span className="material-symbols-outlined text-[20px]">publish</span>
                          </button>
                        )}
                        <button onClick={() => openEditLink(link)} title="Editar" className="text-slate-400 hover:text-primary transition-colors p-1">
                          <span className="material-symbols-outlined text-[20px]">edit</span>
                        </button>
                        <button onClick={() => handleDeleteLink(link.id)} title="Desativar" className="text-slate-400 hover:text-red-500 transition-colors p-1">
                          <span className="material-symbols-outlined text-[20px]">link_off</span>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Leads Tab ── */}
      {activeTab === 'leads' && (
        <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
          <div className="flex flex-wrap justify-between items-center mb-6 gap-4">
            <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">group</span> Pipeline de Leads
            </h2>
            <div className="flex items-center gap-3">
              <select
                className="input text-sm py-2"
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
              >
                <option value="">Todos os status</option>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button onClick={fetchLeads} className="border border-outline-variant hover:bg-surface-container transition-colors text-on-surface px-3 py-2 rounded-full text-sm font-bold font-headline flex items-center gap-1">
                <span className="material-symbols-outlined text-[16px]">refresh</span>
              </button>
            </div>
          </div>

          {isLoadingLeads ? (
            <p className="py-8 text-center text-secondary">Carregando leads...</p>
          ) : leads.length === 0 ? (
            <div className="py-12 text-center">
              <span className="material-symbols-outlined text-5xl text-slate-300 mb-3 block">person_search</span>
              <p className="text-secondary">Nenhum lead encontrado.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table w-full">
                <thead>
                  <tr className="border-b border-outline-variant/30 text-left text-xs text-secondary">
                    <th className="pb-3">Nome / Telefone</th>
                    <th className="pb-3">Link</th>
                    <th className="pb-3">Ref</th>
                    <th className="pb-3">WhatsApp</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3">Data</th>
                    <th className="pb-3 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map(lead => {
                    const s = STATUS_LABELS[lead.status] || { label: lead.status, color: 'bg-slate-100 text-slate-600' };
                    return (
                      <tr key={lead.id} className="border-b border-outline-variant/10 text-sm">
                        <td className="py-4">
                          <p className="font-bold text-on-surface">{lead.name || '—'}</p>
                          <p className="text-xs text-secondary mt-0.5">{lead.phoneE164 || lead.email || '—'}</p>
                        </td>
                        <td className="py-4 text-xs text-secondary">{lead.link?.name || '—'}</td>
                        <td className="py-4 font-mono text-xs text-secondary">{lead.refCode || '—'}</td>
                        <td className="py-4">
                          {lead.conversation ? (
                            <div className="flex flex-col gap-0.5">
                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                lead.conversation.matchConfidence === 'HIGH' ? 'bg-green-100 text-green-700' :
                                lead.conversation.matchConfidence === 'MEDIUM' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {lead.conversation.matchConfidence}
                              </span>
                              {lead.conversation.ctwaClid && (
                                <span className="text-[9px] text-green-600 font-bold">CTWA</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-[10px] text-slate-400">Sem conversa</span>
                          )}
                        </td>
                        <td className="py-4">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${s.color}`}>{s.label}</span>
                        </td>
                        <td className="py-4 text-xs text-secondary">
                          {new Date(lead.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-4 text-right">
                          <select
                            className="text-xs border border-outline-variant/30 rounded-lg px-2 py-1 bg-surface"
                            value={lead.status}
                            onChange={e => handleLeadStatus(lead.id, e.target.value)}
                          >
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <option key={k} value={k}>{v.label}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── WhatsApp Tab ── */}
      {activeTab === 'whatsapp' && (
        <div className="space-y-6">
          <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
                <span className="material-symbols-outlined text-primary">chat</span> WhatsApp Business Accounts
              </h2>
              {user?.roleType === 'ADMIN' && (
                <button onClick={() => { setWabaForm({ wabaId: '', name: '', bsp: 'meta_direct', webhookVerifyToken: '' }); setIsWabaModalOpen(true); }}
                  className="primary-pill-gradient text-white px-4 py-2 rounded-full font-bold font-headline text-sm hover:opacity-90 transition-all flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">add</span> Nova WABA
                </button>
              )}
            </div>

            <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
              <p className="text-xs font-bold text-blue-700 mb-1 flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">info</span> URL do Webhook para configurar no Meta
              </p>
              <div className="flex items-center gap-2">
                <code className="text-xs text-blue-800 font-mono bg-blue-100/50 px-2 py-1 rounded flex-1 break-all">
                  {window.location.origin}/api/webhooks/whatsapp/{user?.id}
                </code>
                <button onClick={() => copyWebhookUrl(user?.id)}
                  className="text-blue-600 hover:text-blue-800 transition-colors p-1 shrink-0">
                  <span className="material-symbols-outlined text-[18px]">content_copy</span>
                </button>
              </div>
              <p className="text-[10px] text-blue-600 mt-1">Use o <strong>webhookVerifyToken</strong> da WABA ao configurar no Meta Business.</p>
            </div>

            {isLoadingWabas ? (
              <p className="py-8 text-center text-secondary">Carregando...</p>
            ) : wabas.length === 0 ? (
              <div className="py-12 text-center">
                <span className="material-symbols-outlined text-5xl text-slate-300 mb-3 block">chat_bubble</span>
                <p className="text-secondary">Nenhuma WABA configurada.</p>
                {user?.roleType !== 'ADMIN' && <p className="text-xs text-slate-400 mt-1">Solicite ao administrador para configurar e atribuir uma conta WhatsApp.</p>}
              </div>
            ) : (
              <div className="space-y-4">
                {wabas.map(waba => (
                  <div key={waba.id} className="border border-outline-variant/30 rounded-xl p-5">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="font-bold text-on-surface">{waba.name}</p>
                        <p className="text-xs text-secondary font-mono mt-0.5">WABA ID: {waba.wabaId}</p>
                        <div className="flex gap-2 mt-1">
                          <span className="px-2 py-0.5 rounded bg-surface-container text-[10px] font-bold">{waba.bsp}</span>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${waba.status === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>{waba.status}</span>
                        </div>
                      </div>
                      {user?.roleType === 'ADMIN' && (
                        <button onClick={() => { setSelectedWabaId(waba.id); setPhoneForm({ phoneNumberId: '', displayPhone: '', accessToken: '' }); setIsPhoneModalOpen(true); }}
                          className="text-xs border border-outline-variant hover:bg-surface-container transition-colors px-3 py-1.5 rounded-full font-bold flex items-center gap-1">
                          <span className="material-symbols-outlined text-[14px]">add</span> Número
                        </button>
                      )}
                    </div>

                    {waba.phoneNumbers?.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2 pt-3 border-t border-outline-variant/20">
                        {waba.phoneNumbers.map(p => (
                          <div key={p.id} className="flex items-center gap-2 bg-surface-container px-3 py-1.5 rounded-lg">
                            <span className="material-symbols-outlined text-green-600 text-[14px]">smartphone</span>
                            <span className="text-xs font-mono font-bold">{p.displayPhone}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── CRM Tab ── */}
      {activeTab === 'crm' && (
        <div className="space-y-6">
          <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="font-headline font-bold text-2xl text-on-surface flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">support_agent</span> Chatwoot CRM
                </h2>
                <p className="text-xs text-secondary mt-1">Contas Chatwoot vinculadas para receber leads automaticamente</p>
              </div>
              {user?.roleType === 'ADMIN' && (
                <button onClick={() => { setCwForm({ chatwootAccountId: '', name: '', apiAccessToken: '' }); setIsCwModalOpen(true); }}
                  className="primary-pill-gradient text-white px-4 py-2 rounded-full font-bold font-headline text-sm hover:opacity-90 transition-all flex items-center gap-2">
                  <span className="material-symbols-outlined text-sm">add</span> Adicionar Conta
                </button>
              )}
            </div>

            <div className="mb-5 p-4 bg-amber-50 border border-amber-100 rounded-xl text-xs text-amber-800 space-y-1">
              <p className="font-bold flex items-center gap-1"><span className="material-symbols-outlined text-[14px]">info</span> Atributos personalizados necessários no Chatwoot</p>
              <p>Crie os seguintes atributos em <strong>Configurações → Atributos Personalizados</strong> para Contatos e Conversas:</p>
              <p className="font-mono text-[10px] bg-amber-100/50 p-2 rounded">
                ref_code · utm_source · utm_medium · utm_campaign · fbclid · gclid · meta_campaign · meta_adset · meta_ad · lp_name · lead_id
              </p>
            </div>

            {isLoadingChatwoot ? (
              <p className="py-8 text-center text-secondary">Carregando...</p>
            ) : chatwootAccounts.length === 0 ? (
              <div className="py-12 text-center">
                <span className="material-symbols-outlined text-5xl text-slate-300 mb-3 block">support_agent</span>
                <p className="text-secondary">Nenhuma conta Chatwoot configurada.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {chatwootAccounts.map(acc => (
                  <div key={acc.id} className="border border-outline-variant/30 rounded-xl p-4 flex items-center justify-between">
                    <div>
                      <p className="font-bold text-on-surface">{acc.name}</p>
                      <p className="text-xs text-secondary font-mono mt-0.5">Account ID: {acc.chatwootAccountId}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${acc.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-500'}`}>
                        {acc.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                      {user?.roleType === 'ADMIN' && (
                        <>
                          <button onClick={() => handleSetupAttributes(acc)}
                            title="Criar atributos personalizados no Chatwoot"
                            className="text-xs font-bold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-1 rounded-lg transition-colors flex items-center gap-1">
                            <span className="material-symbols-outlined text-[14px]">settings</span> Setup Atributos
                          </button>
                          <button onClick={async () => {
                            await conversionsApi.deleteChatwootAccount(acc.id);
                            toast.success('Conta desativada'); fetchChatwootAccounts();
                          }} className="text-slate-400 hover:text-red-500 transition-colors p-1">
                            <span className="material-symbols-outlined text-[18px]">delete</span>
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Webhook URL info */}
          {chatwootWebhookUrl && (
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <p className="text-xs font-bold text-amber-700 mb-2 flex items-center gap-1">
                <span className="material-symbols-outlined text-[14px]">webhook</span>
                URL do Webhook (configure em Chatwoot → Settings → Integrations → Webhooks)
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-[11px] bg-white border border-amber-200 rounded px-2 py-1 font-mono text-amber-900 break-all">{chatwootWebhookUrl}</code>
                <button onClick={() => { navigator.clipboard.writeText(chatwootWebhookUrl); toast.success('URL copiada!'); }}
                  className="shrink-0 text-amber-700 hover:text-amber-900 p-1">
                  <span className="material-symbols-outlined text-[18px]">content_copy</span>
                </button>
              </div>
              <p className="text-[10px] text-amber-600 mt-1">Selecione o evento: <strong>Conversation Updated</strong></p>
            </div>
          )}
        </div>
      )}

      {/* ── Stats Tab ── */}
      {activeTab === 'stats' && (
        <div className="space-y-6">
          {isLoadingStats ? (
            <p className="py-8 text-center text-secondary">Carregando estatísticas...</p>
          ) : stats ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <StatCard icon="group" label="Total Leads" value={stats.total} />
                <StatCard icon="chat" label="Aguard. WhatsApp" value={stats.waiting} color="text-yellow-600" />
                <StatCard icon="mark_chat_read" label="Msg Recebida" value={stats.initiated} color="text-blue-600" />
                <StatCard icon="check_circle" label="Convertidos" value={stats.converted} color="text-green-600" />
                <StatCard icon="percent" label="Taxa de Conversão" value={`${stats.conversionRate}%`} color="text-purple-600" />
                <StatCard icon="currency_exchange" label="Receita Total" value={`R$ ${Number(stats.totalRevenue).toFixed(2)}`} color="text-green-600" />
              </div>

              <div className="bg-surface-container-low rounded-xl p-8 shadow-[0_8px_32px_rgba(25,28,30,0.04)]">
                <h3 className="font-headline font-bold text-lg text-on-surface mb-4">Pipeline de Status</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Aguardando WhatsApp', value: stats.waiting, color: 'bg-yellow-400', total: stats.total },
                    { label: 'Mensagem Recebida', value: stats.initiated, color: 'bg-blue-400', total: stats.total },
                    { label: 'Convertidos', value: stats.converted, color: 'bg-green-400', total: stats.total },
                    { label: 'Perdidos', value: stats.lost, color: 'bg-red-400', total: stats.total }
                  ].map(item => (
                    <div key={item.label}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-secondary">{item.label}</span>
                        <span className="font-bold text-on-surface">{item.value} ({item.total > 0 ? ((item.value / item.total) * 100).toFixed(0) : 0}%)</span>
                      </div>
                      <div className="h-2 bg-surface-container rounded-full overflow-hidden">
                        <div
                          className={`h-full ${item.color} rounded-full transition-all`}
                          style={{ width: item.total > 0 ? `${(item.value / item.total) * 100}%` : '0%' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="py-12 text-center">
              <p className="text-secondary">Clique em "Relatórios" para carregar as estatísticas.</p>
            </div>
          )}
        </div>
      )}

      {/* ── Modal: Create/Edit Link ── */}
      {isLinkModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in shadow-2xl">
            <h3 className="text-xl font-bold font-headline mb-5 text-on-surface">
              {editingLink ? 'Editar Link' : 'Novo Link de Conversão'}
            </h3>
            <form onSubmit={handleLinkSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Nome do Link *</label>
                  <input required className="input w-full text-sm" placeholder="Ex: Campanha Maio 2026"
                    value={linkForm.name} onChange={e => setLinkForm({ ...linkForm, name: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Slug (URL único) *</label>
                  <input required className="input w-full text-sm font-mono" placeholder="campanha-maio-2026"
                    value={linkForm.slug} onChange={e => setLinkForm({ ...linkForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Modo de Funil</label>
                  <select className="input w-full text-sm" value={linkForm.funnelMode}
                    onChange={e => setLinkForm({ ...linkForm, funnelMode: e.target.value })}>
                    {FUNNEL_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Tipo de Redirect</label>
                  <select className="input w-full text-sm" value={linkForm.redirectType}
                    onChange={e => setLinkForm({ ...linkForm, redirectType: e.target.value })}>
                    {REDIRECT_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Conta Meta Ads</label>
                  <select className="input w-full text-sm" value={linkForm.metaAdAccountId}
                    onChange={e => handleMetaAccountChange(e.target.value)}>
                    <option value="">— Nenhuma —</option>
                    {adAccounts.map(a => <option key={a.id} value={a.id}>{a.name} ({a.currency})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Pixel ID (Meta)</label>
                  <input className="input w-full text-sm font-mono" placeholder="123456789"
                    value={linkForm.pixelId} onChange={e => setLinkForm({ ...linkForm, pixelId: e.target.value })} />
                </div>
              </div>

              {linkForm.metaAdAccountId && (
                <div className="grid grid-cols-1 gap-3 p-3 bg-blue-50/50 border border-blue-100 rounded-xl">
                  <p className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">Atribuição de Campanha (opcional)</p>
                  <div>
                    <label className="block text-xs font-bold text-secondary mb-1">Campanha</label>
                    <select className="input w-full text-sm" value={linkForm.metaCampaignId}
                      onChange={e => {
                        const opt = e.target.options[e.target.selectedIndex];
                        handleCampaignChange(e.target.value, opt.dataset.name || '');
                      }}>
                      <option value="">— Selecione uma campanha —</option>
                      {campaigns.map(c => <option key={c.id} value={c.id} data-name={c.name}>{c.name} ({c.status})</option>)}
                    </select>
                  </div>
                  {linkForm.metaCampaignId && (
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Conjunto de Anúncios <span className="text-slate-400 font-normal">(opcional)</span></label>
                      <select className="input w-full text-sm" value={linkForm.metaAdsetId}
                        onChange={e => {
                          const opt = e.target.options[e.target.selectedIndex];
                          handleAdsetChange(e.target.value, opt.dataset.name || '');
                        }}>
                        <option value="">— Nenhum —</option>
                        {adsets.map(s => <option key={s.id} value={s.id} data-name={s.name}>{s.name} ({s.status})</option>)}
                      </select>
                    </div>
                  )}
                  {linkForm.metaAdsetId && (
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Criativo / Anúncio <span className="text-slate-400 font-normal">(opcional)</span></label>
                      <select className="input w-full text-sm" value={linkForm.metaAdId}
                        onChange={e => {
                          const opt = e.target.options[e.target.selectedIndex];
                          setLinkForm(f => ({ ...f, metaAdId: e.target.value, metaAdName: opt.dataset.name || '' }));
                        }}>
                        <option value="">— Nenhum —</option>
                        {ads.map(a => <option key={a.id} value={a.id} data-name={a.name}>{a.name} ({a.status})</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Conta Google Ads</label>
                  <select className="input w-full text-sm" value={linkForm.googleAdsAccountId}
                    onChange={e => setLinkForm({ ...linkForm, googleAdsAccountId: e.target.value })}>
                    <option value="">— Nenhuma —</option>
                    {googleAccounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Número WhatsApp (E.164 sem +)</label>
                  <input className="input w-full text-sm font-mono" placeholder="5511999998888"
                    value={linkForm.whatsappNumber} onChange={e => setLinkForm({ ...linkForm, whatsappNumber: e.target.value.replace(/\D/g, '') })} />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">Número Cloud API (Opcional)</label>
                  <select className="input w-full text-sm" value={linkForm.whatsappPhoneNumberId}
                    onChange={e => setLinkForm({ ...linkForm, whatsappPhoneNumberId: e.target.value })}>
                    <option value="">— Nenhum —</option>
                    {wabaPhones.map(p => <option key={p.id} value={p.id}>{p.displayPhone} ({p.wabaName})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-secondary mb-1">GTM Container ID</label>
                  <input className="input w-full text-sm font-mono" placeholder="GTM-XXXX"
                    value={linkForm.gtmId} onChange={e => setLinkForm({ ...linkForm, gtmId: e.target.value })} />
                </div>
              </div>

              {/* Chatwoot CRM */}
              <div className="grid grid-cols-1 gap-3 p-3 bg-emerald-50/50 border border-emerald-100 rounded-xl">
                <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                  <span className="material-symbols-outlined text-[12px]">support_agent</span> Chatwoot CRM (opcional)
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-bold text-secondary mb-1">Conta Chatwoot</label>
                    <select className="input w-full text-sm" value={linkForm.chatwootAccountId}
                      onChange={e => handleChatwootAccountChange(e.target.value)}>
                      <option value="">— Não integrar com CRM —</option>
                      {chatwootAccounts.filter(a => a.isActive).map(a => (
                        <option key={a.id} value={a.id}>{a.name} (#{a.chatwootAccountId})</option>
                      ))}
                    </select>
                  </div>
                  {linkForm.chatwootAccountId && (
                    <div>
                      <label className="block text-xs font-bold text-secondary mb-1">Caixa de Entrada (Inbox)</label>
                      <select className="input w-full text-sm" value={linkForm.chatwootInboxId}
                        onChange={e => setLinkForm(f => ({ ...f, chatwootInboxId: e.target.value }))}>
                        <option value="">— Selecione a caixa —</option>
                        {chatwootInboxes.map(inbox => (
                          <option key={inbox.id} value={inbox.id}>{inbox.name} ({inbox.channel_type?.replace('Channel::', '') || inbox.channel_type})</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {linkForm.chatwootAccountId && (
                  <p className="text-[10px] text-emerald-600">Leads desta LP serão criados automaticamente como contato + conversa no Chatwoot.</p>
                )}
              </div>

              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Mensagem Pré-preenchida</label>
                <textarea className="input w-full text-sm" rows={3}
                  placeholder="Olá! Vi seu anúncio e tenho interesse. [#REF: {{ref_code}}]"
                  value={linkForm.prefilledMessage} onChange={e => setLinkForm({ ...linkForm, prefilledMessage: e.target.value })} />
                <p className="text-[10px] text-secondary mt-1">Variáveis: {'{{ref_code}}'}, {'{{lead_name}}'}. O código [#REF: X] é adicionado automaticamente.</p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/30">
                <button type="button" onClick={() => setIsLinkModalOpen(false)} className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container transition-colors">Cancelar</button>
                <button type="submit" className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-sm">Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: New WABA ── */}
      {isWabaModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md animate-fade-in shadow-2xl">
            <h3 className="text-xl font-bold font-headline mb-5 text-on-surface">Nova WhatsApp Business Account</h3>
            <form onSubmit={handleWabaSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Nome da Conta *</label>
                <input required className="input w-full text-sm" placeholder="Ex: Clínica GPF"
                  value={wabaForm.name} onChange={e => setWabaForm({ ...wabaForm, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">WABA ID *</label>
                <input required className="input w-full text-sm font-mono" placeholder="ID do WhatsApp Business Account"
                  value={wabaForm.wabaId} onChange={e => setWabaForm({ ...wabaForm, wabaId: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">BSP (Provedor)</label>
                <select className="input w-full text-sm" value={wabaForm.bsp}
                  onChange={e => setWabaForm({ ...wabaForm, bsp: e.target.value })}>
                  <option value="meta_direct">Meta Direto</option>
                  <option value="360dialog">360dialog</option>
                  <option value="twilio">Twilio</option>
                  <option value="gupshup">Gupshup</option>
                  <option value="woztell">WOZTELL</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Webhook Verify Token *</label>
                <input required className="input w-full text-sm font-mono" placeholder="Token secreto para verificar o webhook"
                  value={wabaForm.webhookVerifyToken} onChange={e => setWabaForm({ ...wabaForm, webhookVerifyToken: e.target.value })} />
                <p className="text-[10px] text-secondary mt-1">Crie um token único e use-o ao configurar o webhook no Meta Business.</p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/30">
                <button type="button" onClick={() => setIsWabaModalOpen(false)} className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container transition-colors">Cancelar</button>
                <button type="submit" className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-sm">Criar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Add Chatwoot Account ── */}
      {isCwModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md animate-fade-in shadow-2xl">
            <h3 className="text-xl font-bold font-headline mb-5 text-on-surface">Adicionar Conta Chatwoot</h3>
            <form onSubmit={handleCwSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Nome da Conta *</label>
                <input required className="input w-full text-sm" placeholder="Ex: Clínica Principal"
                  value={cwForm.name} onChange={e => setCwForm({ ...cwForm, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Account ID (Chatwoot) *</label>
                <input required type="number" className="input w-full text-sm font-mono" placeholder="1"
                  value={cwForm.chatwootAccountId} onChange={e => setCwForm({ ...cwForm, chatwootAccountId: e.target.value })} />
                <p className="text-[10px] text-secondary mt-1">Número da conta no Chatwoot (visível na URL: /app/accounts/<strong>ID</strong>/...)</p>
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">API Access Token *</label>
                <input required className="input w-full text-sm font-mono" placeholder="Token de acesso da conta ou agente"
                  value={cwForm.apiAccessToken} onChange={e => setCwForm({ ...cwForm, apiAccessToken: e.target.value })} />
                <p className="text-[10px] text-secondary mt-1">Chatwoot → Configurações → Integrações → API Access Token (use token de agente admin)</p>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/30">
                <button type="button" onClick={() => setIsCwModalOpen(false)} className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container transition-colors">Cancelar</button>
                <button type="submit" className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-sm">Adicionar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Modal: Add Phone Number ── */}
      {isPhoneModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl p-6 w-full max-w-md animate-fade-in shadow-2xl">
            <h3 className="text-xl font-bold font-headline mb-5 text-on-surface">Adicionar Número WhatsApp</h3>
            <form onSubmit={handlePhoneSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Phone Number ID *</label>
                <input required className="input w-full text-sm font-mono" placeholder="ID do número na Meta"
                  value={phoneForm.phoneNumberId} onChange={e => setPhoneForm({ ...phoneForm, phoneNumberId: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Número de Exibição *</label>
                <input required className="input w-full text-sm font-mono" placeholder="+55 11 9 9999-8888"
                  value={phoneForm.displayPhone} onChange={e => setPhoneForm({ ...phoneForm, displayPhone: e.target.value })} />
              </div>
              <div>
                <label className="block text-xs font-bold text-secondary mb-1">Access Token (opcional)</label>
                <input className="input w-full text-sm font-mono" placeholder="Token de acesso do número"
                  value={phoneForm.accessToken} onChange={e => setPhoneForm({ ...phoneForm, accessToken: e.target.value })} />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-outline-variant/30">
                <button type="button" onClick={() => setIsPhoneModalOpen(false)} className="px-4 py-2 rounded-full font-bold text-sm text-secondary hover:bg-surface-container transition-colors">Cancelar</button>
                <button type="submit" className="primary-pill-gradient text-white px-6 py-2 rounded-full font-bold text-sm hover:opacity-90 transition-all shadow-sm">Adicionar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
